import React, { useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { FOLDER_TRASH } from 'pages/media_library/config/settings';
import _ from 'lodash';
import HelperFunctions from '../../global/HelperFunctions';
import { useTranslation } from 'react-i18next';
import { useActiveOrganisation } from 'hooks/useActiveOrganisation';
import { useUploadFileMutation } from 'features/mediaLibrary/mediaApi';

export default function Dropzone({ isDisabled = false, onDropComplete, selectedFolderId }) {
    const { t } = useTranslation('documents');
    const activeOrganisation = useActiveOrganisation();
    const [uploadFile] = useUploadFileMutation();

    const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
        accept: {
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'font/ttf': ['.ttf'],
            'font/otf': ['.otf'],
        },
        onDrop: onDrop,
    });

    const style = useMemo(
        () => ({
            ...baseStyle,
            ...(isDragActive ? activeStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
            ...(isDisabled ? disabledStyle : {}),
        }),
        [isDragActive, isDragReject, isDragAccept, isDisabled],
    );

    if (selectedFolderId === FOLDER_TRASH) {
        // Can't upload files into the trash
        return null;
    }

    return (
        <div {...getRootProps({ style })}>
            <input {...getInputProps()} />
            <div>{t('document.navbar.main.editor.left.blocks.types.fileBlock.fileModal.dragFile')}</div>
        </div>
    );

    function onDrop(acceptedFiles) {
        Promise.all(
            acceptedFiles.map(async (file) => {
                return await uploadFile({
                    file,
                    organisationId: activeOrganisation,
                    folderId: selectedFolderId,
                }).unwrap();
            }),
        ).then((values) => {
            const hasError = values.some((result) => {
                return result.hasOwnProperty('error');
            });

            if (hasError) {
                HelperFunctions.alertModal(
                    t('document.navbar.main.editor.left.blocks.types.fileBlock.fileModal.error'),
                );
            }

            if (_.isFunction(onDropComplete)) {
                onDropComplete(values);
            }
        });
    }
}

const baseStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    borderWidth: 2,
    borderRadius: 2,
    borderColor: '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    color: '#bdbdbd',
    outline: 'none',
    transition: 'border .24s ease-in-out',
};

const activeStyle = {
    borderColor: '#2196f3',
};

const acceptStyle = {
    borderColor: '#00e676',
    backgroundColor: '#00e67614',
};

const rejectStyle = {
    borderColor: '#ff1744',
};

const disabledStyle = {
    opacity: 0.5,
};
